/**
 * SMO Lead'in ekibi için sistem kullanım verisi.
 *
 * İki ayrı kaynağı birleştirir:
 *   1. Canlı durum — `Employees/{id}` üzerindeki nabız alanları (`lastSeen`,
 *      `sessionStartTime`, `totalOnlineMs`, `sessionCount`). Sayfa Yetkileri
 *      sayfasındaki "Çevrimiçi Olanlar" özelliğiyle birebir aynı kural
 *      kullanılır (utils/presence.js), böylece iki ekran çelişmez.
 *   2. Geçmiş — `Employees/{id}/dailyActivity/{gün}` (süre) ve
 *      `Employees/{id}/loginHistory` (girişler).
 *
 * Geçmiş yalnızca panel açıldığında, sabit bir pencere (varsayılan 90 gün) için
 * bir kez çekilir; tarih aralığı filtresi istemci tarafında uygulanır. Böylece
 * tarih kutularıyla oynamak yeni sorgu tetiklemez.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, documentId, getDocs, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { isEmployeeOnline, toMillis } from '../../utils/presence';
import { DAY_MS, dayKeyOf, fetchEmployeeDailyActivity } from '../../utils/onlineActivity';

/** Geçmişin çekildiği pencere. Bundan eskisi raporlanmaz. */
export const ACTIVITY_WINDOW_DAYS = 90;

/** Çevrimiçi/çevrimdışı durumunun yeniden değerlendirilme sıklığı. */
const PRESENCE_TICK_MS = 15 * 1000;

/** Firestore `in` sorgusu sınırı. */
const ID_BATCH_SIZE = 30;

const chunk = (items, size) => {
  const batches = [];
  for (let i = 0; i < items.length; i += size) batches.push(items.slice(i, i + size));
  return batches;
};

export const useTeamActivity = (workers, { enabled, startDate, endDate }) => {
  const [presence, setPresence] = useState({});
  const [history, setHistory] = useState(null); // null = hiç yüklenmedi
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const loadedKeyRef = useRef(null);

  // Çalışan listesi her render'da yeni bir dizi olabildiği için efektlerin
  // bağımlılığı kimlik listesinden türetilir.
  const workerIds = useMemo(
    () => (workers || []).map((w) => w.id).filter(Boolean),
    [workers]
  );
  const workerIdsKey = useMemo(() => [...workerIds].sort().join(','), [workerIds]);

  const windowStartDay = useMemo(
    () => dayKeyOf(Date.now() - ACTIVITY_WINDOW_DAYS * DAY_MS),
    []
  );

  // ── Canlı nabız ────────────────────────────────────────────────────────
  // Panel kapalıyken de dinlenir: bölüm başlığındaki "şu an çevrimiçi" sayısı
  // açmadan görülebilsin diye.
  useEffect(() => {
    if (!workerIds.length) {
      setPresence({});
      return undefined;
    }
    const unsubscribes = chunk(workerIds, ID_BATCH_SIZE).map((batch) =>
      onSnapshot(
        query(collection(db, 'Employees'), where(documentId(), 'in', batch)),
        (snapshot) => {
          setPresence((prev) => {
            const next = { ...prev };
            snapshot.docs.forEach((d) => {
              next[d.id] = d.data();
            });
            return next;
          });
        },
        (err) => console.error('Team presence listener failed:', err)
      )
    );
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerIdsKey]);

  // Nabız bayatladığında kimse yazmadığı için durum kendiliğinden değişmez;
  // "çevrimdışı"ya düşüş ancak saat ilerledikçe fark edilir.
  useEffect(() => {
    const tick = () => setNow(Date.now());
    const interval = setInterval(tick, PRESENCE_TICK_MS);
    const handleVisibility = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // ── Geçmiş (günlük süre + girişler) ────────────────────────────────────
  const loadHistory = useCallback(async () => {
    if (!workerIds.length) {
      setHistory({});
      return;
    }
    setLoading(true);
    setError(null);
    const since = new Date(Date.now() - ACTIVITY_WINDOW_DAYS * DAY_MS);
    try {
      const entries = await Promise.all(
        workerIds.map(async (id) => {
          const [days, loginSnapshot] = await Promise.all([
            fetchEmployeeDailyActivity(id, windowStartDay),
            getDocs(
              query(
                collection(db, 'Employees', id, 'loginHistory'),
                where('timestamp', '>=', since),
                orderBy('timestamp', 'desc')
              )
            ),
          ]);
          const logins = loginSnapshot.docs
            .map((d) => toMillis(d.data().timestamp))
            .filter(Boolean);
          return [id, { days, logins }];
        })
      );
      setHistory(Object.fromEntries(entries));
      loadedKeyRef.current = workerIdsKey;
    } catch (err) {
      console.error('Error fetching team activity:', err);
      setError('Kullanım geçmişi yüklenemedi.');
      setHistory({});
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerIdsKey, windowStartDay]);

  useEffect(() => {
    if (!enabled) return;
    if (loadedKeyRef.current === workerIdsKey) return;
    loadHistory();
  }, [enabled, workerIdsKey, loadHistory]);

  // Ekip değiştiğinde eski geçmiş geçersizdir.
  useEffect(() => {
    if (loadedKeyRef.current && loadedKeyRef.current !== workerIdsKey) {
      setHistory(null);
    }
  }, [workerIdsKey]);

  const reload = useCallback(() => {
    loadedKeyRef.current = null;
    loadHistory();
  }, [loadHistory]);

  // ── Satırlar ───────────────────────────────────────────────────────────
  const rows = useMemo(() => {
    const inRange = (dayKey) => {
      if (startDate && dayKey < startDate) return false;
      if (endDate && dayKey > endDate) return false;
      return dayKey >= windowStartDay;
    };

    return (workers || [])
      .filter((w) => w.id)
      .map((worker) => {
        const employee = presence[worker.id] || {};
        const record = (history && history[worker.id]) || { days: [], logins: [] };

        const online = isEmployeeOnline(employee, now);
        const lastSeen = toMillis(employee.lastSeen);
        const sessionStart = toMillis(employee.sessionStartTime) || toMillis(employee.loginTime);
        const lastLogin = toMillis(employee.lastLogin);
        const lastActivity =
          Math.max(lastSeen || 0, lastLogin || 0, toMillis(employee.logoutTime) || 0, record.logins[0] || 0) || null;

        const daysInRange = record.days
          .filter((d) => inRange(d.date))
          .sort((a, b) => a.date.localeCompare(b.date));
        const rangeMs = daysInRange.reduce((sum, d) => sum + d.onlineMs, 0);
        const activeDays = daysInRange.filter((d) => d.onlineMs > 0).length;
        const loginsInRange = record.logins.filter((ts) => inRange(dayKeyOf(ts)));
        const sessionsInRange = daysInRange.reduce((sum, d) => sum + d.sessions, 0);

        return {
          id: worker.id,
          name: `${worker.name || employee.eName || ''} ${worker.surname || employee.eSurname || ''}`.trim() || '—',
          email: worker.email || employee.eMail || '',
          isOnline: online,
          // Açık oturum henüz günlük sayaca yazılmamış olabilir (5 dk'lık flush).
          currentSessionMs: online && sessionStart ? now - sessionStart : null,
          lastActivity,
          rangeMs,
          activeDays,
          avgActiveDayMs: activeDays > 0 ? rangeMs / activeDays : 0,
          logins: loginsInRange.length,
          sessions: sessionsInRange,
          days: daysInRange,
          totalOnlineMs: typeof employee.totalOnlineMs === 'number' ? employee.totalOnlineMs : 0,
          sessionCount: typeof employee.sessionCount === 'number' ? employee.sessionCount : 0,
        };
      });
  }, [workers, presence, history, now, startDate, endDate, windowStartDay]);

  // ── Özet ───────────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const withActivity = rows.filter((r) => r.rangeMs > 0);
    const totalMs = rows.reduce((sum, r) => sum + r.rangeMs, 0);
    return {
      teamSize: rows.length,
      onlineCount: rows.filter((r) => r.isOnline).length,
      activeCount: withActivity.length,
      idleCount: rows.length - withActivity.length,
      totalMs,
      avgPerPersonMs: rows.length > 0 ? totalMs / rows.length : 0,
      totalLogins: rows.reduce((sum, r) => sum + r.logins, 0),
    };
  }, [rows]);

  // ── Ekip geneli günlük toplam (grafik) ─────────────────────────────────
  const dailyTotals = useMemo(() => {
    const byDay = new Map();
    rows.forEach((row) => {
      row.days.forEach((d) => {
        const current = byDay.get(d.date) || { date: d.date, onlineMs: 0, people: 0 };
        current.onlineMs += d.onlineMs;
        if (d.onlineMs > 0) current.people += 1;
        byDay.set(d.date, current);
      });
    });
    return [...byDay.values()].sort((a, b) => a.date.localeCompare(b.date));
  }, [rows]);

  return {
    rows,
    summary,
    dailyTotals,
    loading,
    error,
    reload,
    hasHistory: history !== null,
    windowStartDay,
  };
};

export default useTeamActivity;
